// Slides in after the user picks a type — replaces the navigate() call entirely.

import { useForm } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import moment from 'moment';
import { bankingService } from "../../services/bankingService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";


// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);
const ChevronDown = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const SearchIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);


const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";



// ─── Label + Input helpers ─────────────────────────────────────────────────────
const FormField = ({ label, children, required }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);







export const CreateTransactionPanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const myPromiseModal = createModal(GeneralModal);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: { date: moment().format('YYYY-MM-DD'), paid_via: 'Cash' }
    });

    // Reset form and state when panel opens OR closes
    useEffect(() => {
        if (isOpen) {
            // Opening fresh
            reset({ date: moment().format('YYYY-MM-DD'), paid_via: 'Cash', amount: '', reference_no: '', bank_charges: '', description: '' });
        } else if (!isOpen) {
            // Cleaning up when closed
            reset({ date: moment().format('YYYY-MM-DD'), paid_via: 'Cash', amount: '', reference_no: '', bank_charges: '', description: '' });
        }
    }, [isOpen]);

    const onSubmit = async (data) => {
        console.log('form_data ', data);
        const result = await myPromiseModal({ title: `Save Transaction for ${selectedType?.transaction_name}`, body: "Would you like to save this transaction?", showInput: false, });
        if (!result?.decision) return;
        setSaving(true);
        try {
            await bankingService.createTransaction({
                ...data,
                account_id: accountId,
                transaction_type_id: selectedType?.module_id || selectedType?.id,
                transaction_name: selectedType?.transaction_name,
            });
            // Clear state immediately on success
            reset({ date: moment().format('YYYY-MM-DD'), paid_via: 'Cash', amount: '', reference_no: '', bank_charges: '', description: '' });
            onSaved?.();
            onClose();
        } catch (err) {
            console.error("Failed to save transaction", err);
        } finally {
            setSaving(false);
        }
    };



    return (
        <div
            className={`fixed top-0 right-0 h-screen w-[480px] bg-white z-50 flex flex-col overflow-hidden
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                        title="Back to type selection"
                    >
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                            New Transaction
                        </p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || '—'}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                >
                    <XIcon />
                </button>
            </div>

            {/* From bank context pill */}
            <div className="px-5 pt-4 pb-1 flex-shrink-0">
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <span className="text-xs text-blue-700 font-medium">
                        From: <span className="font-bold">{bankName || 'Selected Account'}</span>
                    </span>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                <FormField label="Amount" required>
                    <div className="flex">
                        <span className="inline-flex items-center px-3 py-2.5 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-sm font-medium text-gray-500">
                            NGN
                        </span>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            className={`${inputCls} rounded-l-none`}
                            {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Must be > 0' } })}
                        />
                    </div>
                    {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>}
                </FormField>

                <FormField label="Date" required>
                    <input
                        type="date"
                        className={inputCls}
                        {...register('date', { required: 'Date is required' })}
                    />
                    {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date.message}</p>}
                </FormField>

                <FormField label="Reference #">
                    <input
                        type="text"
                        placeholder="e.g. INV-0042"
                        className={inputCls}
                        {...register('reference_no')}
                    />
                </FormField>

                <FormField label="Paid Via">
                    <select className={inputCls} {...register('paid_via')}>
                        <option value="Cash">Cash</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Card">Card</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Other">Other</option>
                    </select>
                </FormField>

                <FormField label="Bank Charges">
                    <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className={inputCls}
                        {...register('bank_charges')}
                    />
                </FormField>

                <FormField label="Description">
                    <textarea
                        rows={3}
                        placeholder="Optional note about this transaction…"
                        className={`${inputCls} resize-none`}
                        {...register('description')}
                    />
                </FormField>

                {/* spacer so last field isn't hidden behind sticky footer */}
                <div className="h-2" />
            </form>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : 'Save Transaction'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};
